import React, { useContext } from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  AccountTreeOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocationOn,
  Map,
} from '@mui/icons-material';
import {
  Typography,
  IconButton,
  Box,
  Paper,
  Divider,
  Tooltip,
  Chip,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import { OrgSettingsContext } from '../../../../../contexts/org-settings-context';
import { IDeliveryChargeRule, DeliveryChargeRuleScope } from '../interfaces';
import { highlightZoneFunc } from '../delivery-charge-overrides';
import { red, yellow } from '@mui/material/colors';
import Decimal from 'decimal.js';
import { MerchantType } from '../../../../../interfaces/delivery-company/merchants';
import AmountChip from './amount-chip';
import { add, CURRENCY, displayMoney } from '../../../../../utils/money';

interface IDistrictNodeData {
  data: {
    name: string;
    rules: IDeliveryChargeRule[];
    highlightZone: highlightZoneFunc;
    expanded: boolean;
    toggleExpand: () => void;
    dimmed: boolean
    editRule: (r:IDeliveryChargeRule)=>void
    merchant: MerchantType
    readonly?:boolean
  };
}

export const DistrictNode = ({ data }: IDistrictNodeData) => {
  const { color } = useContext(OrgSettingsContext);
  const theme = useTheme();
  const secondaryColor = color ? alpha(color, 0.7) : theme.palette.secondary.main;

  const districtRule = data.rules.find(
    (item) =>
      item.scope === DeliveryChargeRuleScope.DISTRICT &&
      item.district === data.name
  );

  const areaRuleCount = data.rules.filter(
    (item) =>
      item.scope === DeliveryChargeRuleScope.AREA &&
      item.area?.district === data.name
  ).length;

  return (
    <Paper
      elevation={3}
      sx={{
        width: 320,
        borderRadius: 3,
        opacity: data.dimmed ? 0.01 : 1,
        filter:  data.dimmed  ? 'blur(1px)' : 'none',
        background: theme.palette.background.paper,
        border: `2px solid ${alpha(secondaryColor, 0.3)}`,
        boxShadow: !data.dimmed ? `0 0 15px ${alpha(yellow[900], 1)}` : undefined,
        fontFamily: theme.typography.fontFamily,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: alpha(secondaryColor, 0.6),
        },
      }}
      onMouseEnter={() =>
        data.highlightZone(DeliveryChargeRuleScope.DISTRICT, data.name)
      }
      onMouseLeave={() =>
        data.highlightZone(DeliveryChargeRuleScope.DISTRICT, '')
      }
    >
      {/* Header - Consistent with governorate but smaller */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        px={2.5}
        py={0.5}
        sx={{
          background: yellow[200],
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AccountTreeOutlined
            fontSize="medium"
            sx={{ 
              color: red[200],
              fontSize: '1.8rem'
            }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            noWrap
            sx={{ 
              color: theme.palette.text.primary,
              fontSize: '1.4rem',
              lineHeight: 1.2
            }}
          >
            {data.name}
          </Typography>

          
        </Stack>

            <IconButton
              onClick={data.toggleExpand}
              size="large" // Extra large button
              sx={{
                color: theme.palette.text.secondary,
                padding: 1.5,
              }}
            >
              {data.expanded ? 
                <KeyboardArrowUp fontSize="large" /> : 
                <KeyboardArrowDown fontSize="large" />
              }
            </IconButton>
      </Stack>

      <Divider sx={{ 
        borderColor: alpha(theme.palette.divider, 0.2),
        borderWidth: 1 
      }} />

      {/* Content - Similar structure but compact */}
      <Box px={2.5} py={2}>
        {/* District Rule */}
        <Box mb={2}>
          <AmountChip
            deltaCharge={districtRule?.delta_charge}
            readonly={data.readonly}
            onClick={()=>{
                const rule = data.rules.find(rule => rule.scope == DeliveryChargeRuleScope.DISTRICT && rule.district == data.name)
                if (rule) 
                    data.editRule(rule);
                else
                    data.editRule({
                        scope: DeliveryChargeRuleScope.DISTRICT,
                        district: data.name,
                        delta_charge: new Decimal(0)
                    })
            }}
          />
          <Box mt={1}>Total Fee <b>{displayMoney(add(data.merchant.default_delivery_fee, districtRule ? [[CURRENCY.USD, districtRule!.delta_charge!]] : []))}</b></Box>
                  
        </Box>

        {/* Rule Counts */}
        <Stack spacing={1.5}>
          <Box display="flex" alignItems="center">
            <LocationOn
              sx={{
                color: theme.palette.text.secondary,
                mr: 1.5,
                fontSize: '1.6rem'
              }}
            />
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'baseline'
              }}
            >
              <Box 
                component="span" 
                fontWeight={700} 
                color="text.primary"
                sx={{ 
                  fontSize: '1.3rem',
                  mr: 1,
                  minWidth: '1.2em',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
              >
                {areaRuleCount}
              </Box>
                City Exceptions
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* React Flow Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-sink"
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          borderRadius: '50%',
          border: '2px solid white',
          top: -5,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      />
    </Paper>
  );
};